import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const MAX_PHOTOS = 6;

export const GalleryUploader = ({ gallery, onChange, testIdPrefix = "gallery" }) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please choose image files only.");
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is over 5MB.`);
        continue;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/uploads", fd);
        let rejected = false;
        onChange((prev) => {
          if (prev.length >= MAX_PHOTOS) {
            rejected = true;
            return prev;
          }
          return [...prev, { path: res.data.path, preview: URL.createObjectURL(file) }];
        });
        if (rejected) toast.error(`Gallery is full — up to ${MAX_PHOTOS} photos.`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail, "Could not upload the image."));
      } finally {
        setUploading(false);
      }
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55">
        Gallery photos <span className="normal-case tracking-normal text-charcoal/35">(optional, up to {MAX_PHOTOS})</span>
      </span>
      <div className="grid grid-cols-3 gap-3">
        {gallery.map((g, i) => (
          <div key={g.path}>
            <div className="group relative">
              <img
                src={g.preview}
                alt={`Gallery photo ${i + 1}`}
                className="aspect-[4/3] w-full rounded-lg border border-stone_warm object-cover"
                data-testid={`${testIdPrefix}-image-${i}`}
              />
              <button
                type="button"
                onClick={() => onChange((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-charcoal/80 text-ivory opacity-0 transition-opacity duration-200 hover:bg-red-600 group-hover:opacity-100"
                data-testid={`${testIdPrefix}-remove-${i}`}
                aria-label="Remove photo"
              >
                <X size={12} />
              </button>
            </div>
            <input
              value={g.caption || ""}
              onChange={(e) => onChange((prev) => prev.map((item, idx) => (idx === i ? { ...item, caption: e.target.value } : item)))}
              placeholder="Caption (optional)"
              className="mt-2 w-full rounded-lg border border-stone_warm bg-white px-3 py-2 text-xs text-charcoal placeholder-charcoal/35 outline-none transition-colors duration-300 focus:border-gold"
              data-testid={`${testIdPrefix}-caption-${i}`}
            />
          </div>
        ))}
        {gallery.length < MAX_PHOTOS && (
          <label
            className="flex aspect-[4/3] cursor-pointer items-center justify-center rounded-lg border border-dashed border-stone_warm bg-[#F3F0EB] text-charcoal/35 transition-colors duration-300 hover:border-gold hover:text-gold-dark"
            data-testid={`${testIdPrefix}-add`}
          >
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFiles}
              disabled={uploading}
              data-testid={`${testIdPrefix}-input`}
            />
          </label>
        )}
      </div>
    </div>
  );
};
